import { selectRowByRowIndex } from '@client-shared/ui-agGrid';
import { fetchActionFactory } from '../../../shared/store/fetchFactory';
import {
  FETCH_BOOK_TO_TAX_DIFFERENCES,
  DELETE_BOOK_TO_TAX_DIFFERENCES,
  DELETE_BOOK_TO_TAX_DIFFERENCES_SUCCESS,
  DELETE_BOOK_TO_TAX_DIFFERENCES_FAILURE,
  BATCH_UPDATE_BOOK_TO_TAX_DIFFERENCES,
  BATCH_UPDATE_BOOK_TO_TAX_DIFFERENCES_SUCCESS,
  BATCH_UPDATE_BOOK_TO_TAX_DIFFERENCES_FAILURE,
  ADD_BOOK_TO_TAX_DIFFERENCES,
  ADD_BOOK_TO_TAX_DIFFERENCES_SUCCESS,
  ADD_BOOK_TO_TAX_DIFFERENCES_FAILURE,
  FETCH_BOOK_TO_TAX_CODE_DESCRIPTIONS,
  UPLOAD_BOOK_TO_TAX_DIFFERENCE,
  UPLOAD_BOOK_TO_TAX_DIFFERENCE_SUCCESS,
  UPLOAD_BOOK_TO_TAX_DIFFERENCE_FAILURE,
  IMPORT_BOOK_TO_TAX_DIFFERENCE,
  IMPORT_BOOK_TO_TAX_DIFFERENCE_SUCCESS,
  IMPORT_BOOK_TO_TAX_DIFFERENCE_FAILURE,
  REFRESH_BOOK_TO_TAX_DIFFERENCES,
  REFRESH_BOOK_TO_TAX_DIFFERENCES_SUCCESS,
  REFRESH_BOOK_TO_TAX_DIFFERENCES_FAILURE,
  CLEAR_UPLOADED_BOOK_TO_TAX_DIFFERENCES,
  FETCH_COUNTRIES,
  SELECT_COUNTRY,
} from './types';

import {
  fetchBookToTaxDifferences as fetchBookToTaxDifferencesApi,
  removeBookToTaxDifference as removeBookToTaxDifferencesApi,
  batchUpdateBookToTaxDifferences as batchUpdateBookToTaxDifferencesApi,
  addBookToTaxDifference as addBookToTaxDifferenceApi,
  fetchBookToTaxCodeDescriptions as fetchBookToTaxCodeDescriptionsApi,
  validateBookToTaxDifferences as validateBookToTaxDifferencesApi,
  importBookToTaxDifferences as importBookToTaxDifferencesApi,
  fetchCountries as fetchCountriesApi,
  refreshBookToTaxDifferences as refreshBookToTaxDifferencesApi,
} from '../api';

import { ModalNames } from '../constants';
import getModalActions from '../../../shared/modals/getModalActions';
import {
  fetchingError,
  fetchConnections,
  fetchAccounts,
  fetchBookTypes,
} from '../../../shared/store/actions';
import { errorNotification, successNotification } from '../../../shared/store/notificationActions';

import emptyRowsTrimmer from '../../../shared/xlsxImport/processors/emptyRowsTrimmer.processor';
import { byField } from '../../../utils/sorting';
import { FIELD_MAPPINGS, MIN_COLUMNS_NUMBER, MAX_COLUMNS_NUMBER } from '../xlsxImport/constants';
import readXlsxFileAsJson from '../../../shared/xlsxImport/readXlsxFileAsJson';
import columnHeaders from '../xlsxImport/columnHeaders';
import excelFileTypeValidator from '../../../shared/xlsxImport/validators/excelFileType.validator';
import columnHeadersValidator from '../../../shared/xlsxImport/validators/columnHeaders.validator';
import currentPeriodValidator from '../../../shared/xlsxImport/validators/currentPeriod.validator';
import noEmptyRowsValidator from '../../../shared/xlsxImport/validators/noEmptyRows.validator';
import calculateImportRangeFactory from '../../../shared/xlsxImport/calculateImportRangeFactory';
import fixDataTypesProcessor from '../xlsxImport/processors/fixDataTypes.processor';
import { ADD_OR_UPDATE_VALUES } from '../../shared/constants';
import { compareBookToTaxDifference } from '../helpers';
import {
  normalizeCellToNullableNumber,
  normalizeCellToString,
} from '../../../utils/readXlsxFile.utils';
import { ALL_OPTION } from '../../../shared/constants';

export const {
  showAction: showBookToTaxDifferenceModalForDelete,
  hideAction: hideBookToTaxDifferenceModalForDelete,
} = getModalActions(ModalNames.bookToTaxDifferenceDeleteModal);

export const fetchBookToTaxDifferences = fetchActionFactory(
  FETCH_BOOK_TO_TAX_DIFFERENCES,
  fetchBookToTaxDifferencesApi,
  { field: 'bookToTaxDifferences' }
);

export const fetchBookToTaxCodeDescriptions = fetchActionFactory(
  FETCH_BOOK_TO_TAX_CODE_DESCRIPTIONS,
  fetchBookToTaxCodeDescriptionsApi,
  { field: 'bookToTaxCodeDescriptions' }
);

export const fetchCountries = fetchActionFactory(
  FETCH_COUNTRIES,
  fetchCountriesApi,
  { field: 'countries' }
);

export const fetchInitialDataForBookToTaxDifferences = periodName => async dispatch => {
  const period = { name: periodName };
  dispatch(fetchAccounts());
  dispatch(fetchConnections(period, {
    includeAllOption: false,
  }));
  dispatch(fetchBookTypes());
  dispatch(fetchCountries());
  // BusinessUnits are loaded using react-query
};

export const selectCountryForBookToTaxDifferences = country => dispatch =>
  dispatch({ type: SELECT_COUNTRY, country });

export const removeBookToTaxDifference = ({
  periodName,
  exceptionId,
}, { rowIndex, clearRangeSelection }) => async dispatch => {
  try {
    dispatch({ type: DELETE_BOOK_TO_TAX_DIFFERENCES });
    await removeBookToTaxDifferencesApi({ exceptionId, periodName });

    dispatch(hideBookToTaxDifferenceModalForDelete());
    await dispatch(fetchBookToTaxDifferences(periodName));
    clearRangeSelection();
    dispatch(selectRowByRowIndex(rowIndex ? rowIndex - 1 : 0));

    dispatch({ type: DELETE_BOOK_TO_TAX_DIFFERENCES_SUCCESS });
    dispatch(successNotification('Book to Tax Difference removed successfully'));
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return dispatch({ type: DELETE_BOOK_TO_TAX_DIFFERENCES_FAILURE });
    }
    throw error;
  }
};

export const batchUpdateBookToTaxDifferences = (
  bookToTaxDifferencesForUpdate,
  { periodName },
  {
    resetEditableGrid,
    findRowIndexBy,
    clearRangeSelection,
  }
) =>
  async dispatch => {
    try {
      dispatch({ type: BATCH_UPDATE_BOOK_TO_TAX_DIFFERENCES });
      await batchUpdateBookToTaxDifferencesApi(bookToTaxDifferencesForUpdate, { periodName });
      dispatch({ type: BATCH_UPDATE_BOOK_TO_TAX_DIFFERENCES_SUCCESS });

      await dispatch(fetchBookToTaxDifferences(periodName));
      dispatch(successNotification('Book to Tax Differences updated successfully'));
      if (findRowIndexBy) {
        clearRangeSelection();

        dispatch(selectRowByRowIndex(findRowIndexBy(
          bookToTaxDifferencesForUpdate[0],
          compareBookToTaxDifference
        )));
      }
    } catch (error) {
      if (error.isFetchingError) {
        dispatch(fetchingError());
        dispatch(errorNotification(error.message));
        dispatch({ type: BATCH_UPDATE_BOOK_TO_TAX_DIFFERENCES_FAILURE });
        resetEditableGrid();
        return;
      }
      throw error;
    }
  };

export const addBookToTaxDifference = (
  bookToTaxDifference,
  { periodName },
  { resetEditableGrid, findRowIndexBy, clearRangeSelection }
) =>
  async dispatch => {
    try {
      dispatch({ type: ADD_BOOK_TO_TAX_DIFFERENCES });
      await addBookToTaxDifferenceApi(bookToTaxDifference, { periodName });
      dispatch({ type: ADD_BOOK_TO_TAX_DIFFERENCES_SUCCESS });

      await dispatch(fetchBookToTaxDifferences(periodName));
      dispatch(successNotification('Book to Tax Differences added successfully'));
      clearRangeSelection();
      dispatch(selectRowByRowIndex(findRowIndexBy(
        bookToTaxDifference,
        compareBookToTaxDifference
      )));
    } catch (error) {
      if (error.isFetchingError) {
        dispatch(fetchingError());
        dispatch(errorNotification(error.message));
        dispatch({ type: ADD_BOOK_TO_TAX_DIFFERENCES_FAILURE });
        resetEditableGrid();
        return;
      }
      throw error;
    }
  };

export const uploadBookToTaxDifferences = (file, currentPeriod, bookToTaxDifferences, accounts) =>
  async dispatch => {
    dispatch({
      type: UPLOAD_BOOK_TO_TAX_DIFFERENCE,
    });

    try {
      const uploadedBookToTaxDifferences = await readXlsxFileAsJson({
        file,
        fileValidators: [excelFileTypeValidator],
        prevalidators: [columnHeadersValidator],
        postvalidators: [noEmptyRowsValidator, currentPeriodValidator],
        fieldMappings: FIELD_MAPPINGS,
        sheetName: 'BOOK_TO_TAX_DIFFERENCE',
        getRange: calculateImportRangeFactory('K'),
        preprocessors: [emptyRowsTrimmer, fixDataTypesProcessor],
        mapToJsonOptions: {
          header: columnHeaders,
        },
        validationData: {
          columnHeaders,
          currentPeriod,
          minColumnsNumber: MIN_COLUMNS_NUMBER,
          maxColumnsNumber: MAX_COLUMNS_NUMBER,
        },
        fieldNormalizers: {
          period: normalizeCellToString,
          enabledflag: normalizeCellToString,
          exceptionaccount: normalizeCellToString,
          exceptionbasis: normalizeCellToString,
          booktotaxcodedescription: normalizeCellToString,
          connections: normalizeCellToString,
          businessUnits: normalizeCellToString,
          excludedcompanycodes: normalizeCellToString,
          countrytaxtypes: normalizeCellToString,
          exceptionid: normalizeCellToNullableNumber,
        },
      });

      const extendedBookToTaxDifferences = uploadedBookToTaxDifferences.map(uploadedBookToTaxDifference => {
        const foundAccount = accounts.find(({ value }) => value === uploadedBookToTaxDifference.exceptionAccount);
        const uploadedConnectionsLength = uploadedBookToTaxDifference.connections.length;
        const uploadedBusinessUnitsLength = uploadedBookToTaxDifference.businessUnits.length;

        return ({
          ...uploadedBookToTaxDifference,
          description: foundAccount ? foundAccount.label : '',
          connections: !uploadedConnectionsLength && !uploadedBusinessUnitsLength
            ? [ALL_OPTION]
            : uploadedBookToTaxDifference.connections,
          businessUnits: uploadedBookToTaxDifference.businessUnits,
        });
      });

      const validationErrors = await validateBookToTaxDifferencesApi(
        extendedBookToTaxDifferences,
        { periodName: currentPeriod.name }
      );

      if (validationErrors) {
        Object.keys(validationErrors).forEach(errorIndex => {
          extendedBookToTaxDifferences[errorIndex].validationData = validationErrors[errorIndex];
        });
      }

      const sortedUploadedBookToTaxDifferences = extendedBookToTaxDifferences.sort(byField('exceptionAccount'));

      dispatch({
        type: UPLOAD_BOOK_TO_TAX_DIFFERENCE_SUCCESS,
        uploadedBookToTaxDifferences: sortedUploadedBookToTaxDifferences,
      });
      dispatch(successNotification('File uploaded successfully'));
    } catch (error) {
      dispatch(errorNotification(error.message));
      dispatch({
        type: UPLOAD_BOOK_TO_TAX_DIFFERENCE_FAILURE,
      });
    }
  };

export const importBookToTaxDifferences = (
  bookToTaxDifferences,
  { periodName }
) => async dispatch => {
  const mappedBookToTaxDifferences = bookToTaxDifferences
    .filter(({ addOrUpdate }) => addOrUpdate !== ADD_OR_UPDATE_VALUES.NO_CHANGE);
  dispatch({
    type: IMPORT_BOOK_TO_TAX_DIFFERENCE,
  });
  try {
    await importBookToTaxDifferencesApi(mappedBookToTaxDifferences, { periodName });
    dispatch({
      type: IMPORT_BOOK_TO_TAX_DIFFERENCE_SUCCESS,
    });
    dispatch(successNotification('Book to Tax Differences imported successfully'));
  } catch (error) {
    dispatch(errorNotification(error.message));
    dispatch({
      type: IMPORT_BOOK_TO_TAX_DIFFERENCE_FAILURE,
    });
  }
};

export const refreshBookToTaxDifferences = ({ periodName }) => async dispatch => {
  try {
    dispatch({
      type: REFRESH_BOOK_TO_TAX_DIFFERENCES,
    });
    await refreshBookToTaxDifferencesApi({ periodName });
    dispatch({
      type: REFRESH_BOOK_TO_TAX_DIFFERENCES_SUCCESS,
    });
    dispatch(successNotification('Book to Tax Differences refreshed successfully'));
  } catch (error) {
    dispatch(errorNotification(error.message));
    dispatch({
      type: REFRESH_BOOK_TO_TAX_DIFFERENCES_FAILURE,
    });
  }
};

export const clearUploadedBookToTaxDifferences = () => async dispatch =>
  dispatch({
    type: CLEAR_UPLOADED_BOOK_TO_TAX_DIFFERENCES,
  });
